import {useInfiniteQuery} from '@tanstack/react-query';
import {useMemo} from 'react';
import {ConversationMessage} from '@/types/conversationMessage';
import conversationsFetchers from '@/api/fetchers/conversations';

export const CONVERSATION_MESSAGES_QUERY_KEY = 'conversationMessages';

/**
 * Fetches paginated conversation messages using cursor-based pagination.
 *
 * Without a cursor, the endpoint returns all messages in ascending order (oldest first).
 * With a cursor (a created_at timestamp), it returns messages older than that timestamp,
 * ordered descending, limited by per_page. The "backwards" direction reverses the result
 * back to ascending order. We use the oldest message's createdAt as the next cursor to
 * fetch the previous (older) page when scrolling up.
 *
 * Messages are deduplicated by ID to handle potential page overlaps.
 */
function useConversationMessagesQuery(
  conversationId: string | null,
  direction: 'backwards' | 'forwards' = 'backwards'
) {
  const response = useInfiniteQuery({
    queryKey: [CONVERSATION_MESSAGES_QUERY_KEY, conversationId],
    queryFn: ({pageParam}: {pageParam?: string}) =>
      conversationsFetchers.messages({
        conversationId: conversationId as string,
        cursor: pageParam,
        direction,
      }),
    enabled: !!conversationId,
    // The endpoint uses cursor-based pagination with created_at timestamps.
    // Without a cursor, it returns all messages in ascending order.
    // With a cursor, it returns messages older than the cursor date (created_at < cursor),
    // ordered desc, limited by per_page. "backwards" reverses the result to ascending.
    // We use the oldest message's createdAt as the next cursor to fetch the previous page.
    getNextPageParam: (lastPage: ConversationMessage[]) => {
      if (!lastPage.length) {
        return undefined;
      }
      return lastPage[0]?.createdAt;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    staleTime: Infinity,
  });

  const messages = useMemo(() => {
    if (!response.data) {
      return [];
    }
    // Backend returns messages sorted by created_at ascending.
    // Dedup by id in case pages overlap.
    const allMessages = response.data.pages.flatMap((page) => page);
    const uniqueById = new Map(allMessages.map((msg) => [msg.id, msg]));
    return Array.from(uniqueById.values());
  }, [response.data]);

  return {
    ...response,
    messages,
  };
}

export default useConversationMessagesQuery;
